import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { DataTable, TablePagination } from '@cargomatic/ui';
import UserStore from 'stores/UserStore';
import UserActionCreators from 'actions/UserActionCreators';
import { getAdminUsersColumns } from './AdminUsersColumns';

const PER_PAGE = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export function AdminUsersTable({
  status,
  search,
  selectedIds,
  onSelectedChange,
  refreshKey,
}) {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sorting, setSorting] = useState([]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: PER_PAGE,
  });

  useEffect(() => {
    const handleStoreChange = () => {
      const data = UserStore.getAdminUsers() || [];
      if (!data[0]) {
        setLoading(false);
        return;
      }
      const { pages: nextPages, total: nextTotal, users: nextUsers } = data[0];
      const structured = (nextUsers || []).map((u) => ({
        ...u,
        first_name: (u.first_name || '').trim(),
        last_name: (u.last_name || '').trim(),
        name: `${(u.first_name || '').trim()} ${(u.last_name || '').trim()}`.trim(),
      }));
      setUsers(structured);
      setPages(nextPages);
      setTotal(nextTotal || 0);
      setLoading(false);
    };
    UserStore.addChangeListener(handleStoreChange);
    return () => UserStore.removeChangeListener(handleStoreChange);
  }, []);

  const prevFiltersRef = useRef({ status, search });

  useEffect(() => {
    const nextFilters = { status, search };
    const filtersChanged =
      JSON.stringify(prevFiltersRef.current) !== JSON.stringify(nextFilters);
    prevFiltersRef.current = nextFilters;

    if (filtersChanged && pagination.pageIndex !== 0) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      return;
    }

    setLoading(true);
    const params = {
      page: pagination.pageIndex + 1,
      per_page: pagination.pageSize,
      paginate: true,
    };
    if (search) params.search = search;
    if (status?.length === 1) {
      params.active = status[0] === 'active';
    }
    if (sorting?.length) {
      params.sort_by = sorting[0].id;
      params.order = sorting[0].desc ? 'desc' : 'asc';
    }
    UserActionCreators.getAdminUsers(params);
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    JSON.stringify(status),
    search,
    refreshKey,
    JSON.stringify(sorting),
  ]);

  const columns = useMemo(() => getAdminUsersColumns(), []);

  const rowSelection = useMemo(() => {
    const map = {};
    (selectedIds || []).forEach((id) => {
      map[id] = true;
    });
    return map;
  }, [selectedIds]);

  const table = useReactTable({
    data: users,
    columns,
    state: { sorting, pagination, rowSelection },
    pageCount: pages,
    manualPagination: true,
    manualSorting: true,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onRowSelectionChange: (updater) => {
      const next =
        typeof updater === 'function' ? updater(rowSelection) : updater;
      const nextIds = Object.keys(next).filter((k) => next[k]);
      onSelectedChange?.(nextIds);
    },
    getRowId: (row) => row._id,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const selectedCount = selectedIds?.length || 0;

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <DataTable
          table={table}
          isLoading={loading}
          emptyResultsText="No admin users found."
        />
      </div>
      <TablePagination
        page={pagination.pageIndex + 1}
        pages={pages}
        total={total}
        perPage={pagination.pageSize}
        onPageChange={(p) => table.setPageIndex(p - 1)}
        selectedCount={selectedCount > 0 ? selectedCount : undefined}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageSizeChange={(size) => table.setPageSize(size)}
      />
    </div>
  );
}
